import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { ILeaseReminder } from 'src/models/dlcLease';
import * as styles from './styles';
import useLatestRequest from '../useLatestRequest';

interface ILeaseReminderModalProps {
    isOpen: boolean;
    leaseNumber: number;
    reminders: ILeaseReminder[];
    onClose: () => void;
    onRemindersChanged: (reminders: ILeaseReminder[]) => void;
}

interface IReminderLandmanOption {
    tsdNo: number;
    akaName: string;
}

const toDateInputValue = (value?: Date | string): string => (value ? String(value).slice(0, 10) : '');

const tomorrow = (): string => {
    const value = new Date();
    value.setDate(value.getDate() + 1);
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
};

const reminderKey = (reminder: ILeaseReminder): string => `${toDateInputValue(reminder.remindDate)}:${reminder.remindTsdNo}`;

const LeaseReminderModal: React.FC<ILeaseReminderModalProps> = ({ isOpen, leaseNumber, reminders, onClose, onRemindersChanged }) => {
    const { get, put, del, response } = useFetch();
    const [remindDate, setRemindDate] = useState('');
    const [landmanNumber, setLandmanNumber] = useState('');
    const [landmanSearch, setLandmanSearch] = useState('');
    const [landmanOptions, setLandmanOptions] = useState<IReminderLandmanOption[]>([]);
    const [isLandmanPickerOpen, setIsLandmanPickerOpen] = useState(false);
    const [note, setNote] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isWorking, setIsWorking] = useState(false);
    const [selectedReminder, setSelectedReminder] = useState<ILeaseReminder | undefined>();
    const landmanValidationRequests = useLatestRequest();
    const landmanSearchRequests = useLatestRequest();
    const mutationRequests = useLatestRequest();

    const validateLandman = async (value: string): Promise<boolean> => {
        const requestId = landmanValidationRequests.begin();
        if (!value || Number(value) <= 0) {
            return false;
        }
        const result = await get(`/v1.0/leases/reminders/landman-name/${value}`);
        if (!landmanValidationRequests.isCurrent(requestId)) {
            return false;
        }
        if (response.ok && result?.landmanName) {
            setLandmanSearch(`${value} - ${result.landmanName}`);
            setErrorMessage('');
            return true;
        }
        setErrorMessage('No person was found for that Landman Number.');
        return false;
    };

    useEffect(() => {
        if (!isOpen || !isLandmanPickerOpen) {
            landmanSearchRequests.invalidate();
            return undefined;
        }
        const timer = window.setTimeout(async () => {
            const requestId = landmanSearchRequests.begin();
            const result = await get(`/v1.0/leases/reminders/landmen?search=${encodeURIComponent(landmanSearch.trim())}`);
            if (landmanSearchRequests.isCurrent(requestId)) {
                setLandmanOptions(Array.isArray(result) ? result : []);
            }
        }, 300);
        return () => {
            window.clearTimeout(timer);
            landmanSearchRequests.invalidate();
        };
    }, [get, isLandmanPickerOpen, isOpen, landmanSearch, landmanSearchRequests]);

    useEffect(() => {
        mutationRequests.invalidate();
        if (!isOpen) {
            return;
        }
        setSelectedReminder(undefined);
        setRemindDate('');
        setLandmanNumber('');
        setLandmanSearch('');
        setLandmanOptions([]);
        setIsLandmanPickerOpen(false);
        setNote('');
        setErrorMessage('');
        // Reset the form whenever a different lease is opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, leaseNumber, mutationRequests]);

    if (!isOpen) {
        return null;
    }

    const editReminder = (reminder: ILeaseReminder): void => {
        setSelectedReminder(reminder);
        setRemindDate(toDateInputValue(reminder.remindDate));
        setLandmanNumber(reminder.remindTsdNo?.toString() || '');
        setLandmanSearch(reminder.remindTsdNo?.toString() || '');
        setNote(reminder.remindNote || '');
        setErrorMessage('');
        if (reminder.remindTsdNo) {
            void validateLandman(reminder.remindTsdNo.toString());
        }
    };

    const addReminder = (): void => {
        setSelectedReminder(undefined);
        setRemindDate('');
        setLandmanNumber('');
        setLandmanSearch('');
        setLandmanOptions([]);
        setIsLandmanPickerOpen(false);
        setNote('');
        setErrorMessage('');
    };

    const save = async (): Promise<void> => {
        const retainsExistingDate = !!selectedReminder && remindDate === toDateInputValue(selectedReminder.remindDate);
        if (!remindDate || (!retainsExistingDate && remindDate < tomorrow())) {
            setErrorMessage('Reminder date must be at least one day in the future.');
            return;
        }
        if (!(await validateLandman(landmanNumber))) {
            return;
        }
        if (note.length > 2000) {
            setErrorMessage('Reminder note cannot exceed 2,000 characters.');
            return;
        }

        setIsWorking(true);
        const requestId = mutationRequests.begin();
        const originalQuery = selectedReminder
            ? `?originalRemindDate=${encodeURIComponent(toDateInputValue(selectedReminder.remindDate))}&originalTsdNo=${selectedReminder.remindTsdNo}`
            : '';
        const saved = await put(`/v1.0/leases/master/${leaseNumber}/reminder${originalQuery}`, {
            lsNo: leaseNumber,
            remindDate,
            remindTsdNo: Number(landmanNumber),
            remindNote: note,
        });
        if (!mutationRequests.isCurrent(requestId)) {
            return;
        }
        setIsWorking(false);
        if (!response.ok) {
            setErrorMessage(saved?.message || 'The reminder could not be saved.');
            return;
        }
        const nextReminders = selectedReminder
            ? reminders.map((item) => (reminderKey(item) === reminderKey(selectedReminder) ? (saved as ILeaseReminder) : item))
            : [...reminders, saved as ILeaseReminder];
        onRemindersChanged(nextReminders);
        editReminder(saved as ILeaseReminder);
    };

    const remove = async (): Promise<void> => {
        if (!selectedReminder?.remindDate || !selectedReminder.remindTsdNo) {
            return;
        }
        if (!window.confirm('Delete this lease reminder?')) {
            return;
        }
        setIsWorking(true);
        const requestId = mutationRequests.begin();
        const result = await del(
            `/v1.0/leases/master/${leaseNumber}/reminder?remindDate=${encodeURIComponent(
                toDateInputValue(selectedReminder.remindDate)
            )}&remindTsdNo=${selectedReminder.remindTsdNo}`
        );
        if (!mutationRequests.isCurrent(requestId)) {
            return;
        }
        setIsWorking(false);
        if (!response.ok) {
            setErrorMessage(result?.message || 'The reminder could not be deleted.');
            return;
        }
        onRemindersChanged(reminders.filter((item) => reminderKey(item) !== reminderKey(selectedReminder)));
        addReminder();
    };

    return (
        <div style={modalStyles.overlay} role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div style={modalStyles.modal} role="dialog" aria-modal="true" aria-labelledby="lease-reminder-title">
                <div style={modalStyles.header}>
                    <h2 id="lease-reminder-title" style={modalStyles.title}>
                        Lease Reminders
                    </h2>
                    <button type="button" style={modalStyles.closeButton} onClick={onClose} aria-label="Close">
                        &times;
                    </button>
                </div>
                <div style={modalStyles.body}>
                    <div style={modalStyles.listHeader}>
                        <strong>Existing Reminders</strong>
                        <button type="button" style={styles.buttonSecondary} onClick={addReminder}>
                            Add Reminder
                        </button>
                    </div>
                    {reminders.length === 0 ? (
                        <div style={modalStyles.empty}>No reminders have been added to this lease.</div>
                    ) : (
                        <div style={modalStyles.list}>
                            {[...reminders]
                                .sort((a, b) => toDateInputValue(b.remindDate).localeCompare(toDateInputValue(a.remindDate)))
                                .map((item) => (
                                    <button
                                        type="button"
                                        key={reminderKey(item)}
                                        style={{
                                            ...modalStyles.listItem,
                                            ...(selectedReminder && reminderKey(item) === reminderKey(selectedReminder)
                                                ? modalStyles.listItemSelected
                                                : {}),
                                        }}
                                        onClick={() => editReminder(item)}
                                    >
                                        <span>{toDateInputValue(item.remindDate)}</span>
                                        <span>Landman #{item.remindTsdNo}</span>
                                        <span style={modalStyles.listNote}>{item.remindNote || 'No note'}</span>
                                    </button>
                                ))}
                        </div>
                    )}
                    <strong>{selectedReminder ? 'Edit Reminder' : 'Add Reminder'}</strong>
                    <label style={modalStyles.label}>
                        Reminder Date
                        <LeaseRecordInput
                            type="date"
                            min={tomorrow()}
                            value={remindDate}
                            onChange={(e) => setRemindDate(e.target.value)}
                            style={modalStyles.input}
                        />
                    </label>
                    <div style={modalStyles.landmanRow}>
                        <label style={{ ...modalStyles.label, flex: '1 1 auto' }}>
                            Landman
                            <div style={modalStyles.picker}>
                                <LeaseRecordInput
                                    type="text"
                                    value={landmanSearch}
                                    placeholder="Search by landman name or number..."
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        landmanValidationRequests.invalidate();
                                        setLandmanSearch(value);
                                        setLandmanNumber(/^\d+$/.test(value.trim()) ? value.trim() : '');
                                        setIsLandmanPickerOpen(true);
                                    }}
                                    onFocus={() => setIsLandmanPickerOpen(true)}
                                    style={modalStyles.input}
                                    autoComplete="off"
                                />
                                {isLandmanPickerOpen && (
                                    <div style={modalStyles.options}>
                                        {landmanOptions.length === 0 && <div style={modalStyles.emptyOption}>No matching landmen</div>}
                                        {landmanOptions.map((option) => (
                                            <button
                                                type="button"
                                                key={option.tsdNo}
                                                style={modalStyles.option}
                                                onClick={() => {
                                                    setLandmanNumber(option.tsdNo.toString());
                                                    setLandmanSearch(`${option.tsdNo} - ${option.akaName}`);
                                                    setIsLandmanPickerOpen(false);
                                                    setErrorMessage('');
                                                }}
                                            >
                                                {option.tsdNo} - {option.akaName}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </label>
                    </div>
                    <label style={modalStyles.label}>
                        Reminder Note
                        <textarea value={note} maxLength={2000} rows={6} onChange={(e) => setNote(e.target.value)} style={modalStyles.textarea} />
                        <span style={modalStyles.count}>{note.length}/2000</span>
                    </label>
                    {errorMessage && (
                        <div role="alert" style={modalStyles.error}>
                            {errorMessage}
                        </div>
                    )}
                </div>
                <div style={modalStyles.footer}>
                    <div>
                        {selectedReminder && (
                            <button type="button" style={modalStyles.deleteButton} disabled={isWorking} onClick={() => void remove()}>
                                Delete
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button type="button" style={styles.buttonSecondary} disabled={isWorking} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="button" style={styles.buttonPrimary} disabled={isWorking} onClick={() => void save()}>
                            {isWorking ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const modalStyles: Record<string, React.CSSProperties> = {
    overlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        background: 'rgba(17, 24, 39, 0.48)',
    },
    modal: { width: 'min(42rem, 100%)', borderRadius: '8px', background: styles.colors.white, boxShadow: '0 20px 45px rgba(0,0,0,.22)' },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.9rem 1rem',
        borderBottom: `1px solid ${styles.colors.gray200}`,
    },
    title: { margin: 0, fontSize: '1rem', fontWeight: 600 },
    closeButton: { border: 0, background: 'transparent', fontSize: '1.5rem', lineHeight: 1, cursor: 'pointer' },
    body: { display: 'flex', flexDirection: 'column', gap: '0.9rem', padding: '1rem' },
    listHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    list: { maxHeight: '11rem', overflowY: 'auto', border: `1px solid ${styles.colors.gray200}`, borderRadius: '4px' },
    listItem: {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '7rem 9rem 1fr',
        gap: '0.75rem',
        padding: '0.55rem',
        border: 0,
        borderBottom: `1px solid ${styles.colors.gray200}`,
        background: styles.colors.white,
        color: styles.colors.gray700,
        textAlign: 'left',
        cursor: 'pointer',
    },
    listItemSelected: { background: '#EEF2FF' },
    listNote: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    empty: { padding: '0.75rem', borderRadius: '4px', background: styles.colors.gray100, color: styles.colors.gray500 },
    label: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.3rem',
        color: styles.colors.gray700,
        fontSize: styles.typography.fontSize.sm,
        fontWeight: 500,
    },
    input: { minHeight: '2.25rem', padding: '0.4rem 0.55rem', border: `1px solid ${styles.colors.gray300}`, borderRadius: '4px', font: 'inherit' },
    textarea: { resize: 'vertical', padding: '0.55rem', border: `1px solid ${styles.colors.gray300}`, borderRadius: '4px', font: 'inherit' },
    landmanRow: { display: 'flex', alignItems: 'flex-end', gap: '0.75rem' },
    picker: { position: 'relative', display: 'flex', flexDirection: 'column' },
    options: {
        position: 'absolute',
        top: '2.35rem',
        left: 0,
        right: 0,
        zIndex: 5,
        maxHeight: '12rem',
        overflowY: 'auto',
        border: `1px solid ${styles.colors.gray300}`,
        borderRadius: '4px',
        background: styles.colors.white,
        boxShadow: '0 8px 16px rgba(0,0,0,.12)',
    },
    option: {
        width: '100%',
        padding: '0.6rem',
        border: 0,
        borderBottom: `1px solid ${styles.colors.gray200}`,
        background: styles.colors.white,
        textAlign: 'left',
        cursor: 'pointer',
    },
    emptyOption: { padding: '0.6rem', color: styles.colors.gray500, fontWeight: 400 },
    count: { alignSelf: 'flex-end', color: styles.colors.gray500, fontSize: styles.typography.fontSize.xs },
    error: { padding: '0.6rem', borderRadius: '4px', color: '#991B1B', background: '#FEE2E2', fontSize: styles.typography.fontSize.sm },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.8rem 1rem',
        borderTop: `1px solid ${styles.colors.gray200}`,
    },
    deleteButton: { ...styles.buttonSecondary, color: '#991B1B', borderColor: '#FCA5A5' },
};

export default LeaseReminderModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/LeaseReminderModal.tsx